// Pure, store-free KPI metric helpers. Kept separate from `useKpiMetric.ts`
// (which pulls in Pinia stores) so the reducer math stays isolated.

export type MetricType = "options_share" | "average" | "nps";
export type MetricUnit = "percent" | "nps" | "scale";

export interface KpiMetric {
  type: MetricType;
  question_id?: number | null;
  option_codes?: string[];
  color?: string | null;
}

interface AnswerSlice {
  nb?: number;
  weighted_nb?: number;
}

// chartsData[questionId] (the "All" slice) or its pop_sg_* sub-slice.
export interface ChartDataSlice {
  respondents?: number;
  weighted_respondents?: number;
  answers?: Record<string, AnswerSlice>;
}

export interface MetricResult {
  value: number;
  unit: MetricUnit;
  n: number;
}

const NPS_PROMOTERS = ["9", "10"];
const NPS_DETRACTORS = ["0", "1", "2", "3", "4", "5", "6"];
// Universal "skipped / no answer" sentinel code (see survey_results/* on the API).
// It must never count towards a numeric average.
const SKIP_CODE = "999";

export function metricTypesFor(cardType: string | null | undefined): MetricType[] {
  const types: MetricType[] = ["options_share"];
  if (cardType === "q_rating" || cardType === "q_nps") types.push("average");
  if (cardType === "q_nps") types.push("nps");
  return types;
}

export function unitForType(type: MetricType): MetricUnit {
  if (type === "options_share") return "percent";
  if (type === "nps") return "nps";
  return "scale";
}

/** Reduce a single metric definition against one population's raw chart data. */
export function computeMetric(
  metric: KpiMetric | null | undefined,
  source: ChartDataSlice | null | undefined,
  withWeights = false,
): MetricResult | null {
  if (!metric || !source?.answers) return null;
  const answers = source.answers;
  const base = (withWeights ? source.weighted_respondents : source.respondents) || 0;
  const count = (code: string) => (withWeights ? answers[code]?.weighted_nb : answers[code]?.nb) || 0;
  const sumCodes = (codes: string[]) => codes.reduce((sum, code) => sum + count(code), 0);

  switch (metric.type) {
    case "options_share": {
      if (base === 0) return null;
      const codes = metric.option_codes || [];
      return { value: (sumCodes(codes) / base) * 100, unit: "percent", n: base };
    }
    case "average": {
      let totalN = 0;
      let weightedSum = 0;
      for (const code of Object.keys(answers)) {
        if (code === SKIP_CODE) continue;
        const num = Number(code);
        if (Number.isNaN(num)) continue;
        const n = count(code);
        totalN += n;
        weightedSum += num * n;
      }
      if (totalN === 0) return null;
      return { value: weightedSum / totalN, unit: "scale", n: totalN };
    }
    case "nps": {
      if (base === 0) return null;
      const promoters = sumCodes(NPS_PROMOTERS);
      const detractors = sumCodes(NPS_DETRACTORS);
      return { value: ((promoters - detractors) / base) * 100, unit: "nps", n: base };
    }
    default:
      return null;
  }
}

/** Format a reduced metric value for display. */
export function formatMetricValue(value: number | null | undefined, unit?: MetricUnit): string {
  if (value == null) return "—";
  if (unit === "percent") return `${Math.round(value)}%`;
  if (unit === "nps") return `${value > 0 ? "+" : ""}${Math.round(value)}`;
  if (unit === "scale") return value.toFixed(1);
  return String(Math.round(value));
}
